import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppShell } from "@/components/AppShell";
import { TransactionForm } from "@/components/TransactionForm";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";
import { toDMY } from "@/lib/dates";

export const CONFIGS = {
  sale: {
    type: "sale",
    title: "Sales",
    singular: "Sale",
    partyLabel: "Customer",
    partyMaster: "customer",
    hint: "Stock is reduced when a sale is saved",
    effect: "Reduces stock",
  },
  sales_order: {
    type: "sales_order",
    title: "Sales Orders",
    singular: "Sales Order",
    partyLabel: "Customer",
    partyMaster: "customer",
    hint: "No stock change until the order is converted to a sale",
    effect: "No stock change until converted",
    convertible: "sale",
  },
  purchase: {
    type: "purchase",
    title: "Purchase",
    singular: "Purchase",
    partyLabel: "Supplier",
    partyMaster: "supplier",
    hint: "Stock is added when a purchase is saved",
    effect: "Adds stock",
  },
  purchase_order: {
    type: "purchase_order",
    title: "Purchase Orders",
    singular: "Purchase Order",
    partyLabel: "Supplier",
    partyMaster: "supplier",
    hint: "No stock change until the order is converted to a purchase",
    effect: "No stock change until converted",
    convertible: "purchase",
  },
  adjustment: {
    type: "adjustment",
    title: "Stock Adjustment",
    singular: "Adjustment",
    partyLabel: null,
    partyMaster: null,
    hint: "Use positive numbers to add stock and negative to reduce",
    effect: "Adds or reduces stock directly",
  },
  transfer: {
    type: "transfer",
    title: "Stock Transfer",
    singular: "Stock Transfer",
    partyLabel: null,
    partyMaster: null,
    hint: "Moves stock from one material centre to another",
    effect: "Moves stock between centres",
  },
};

export default function Transactions({ configKey }) {
  const config = CONFIGS[configKey];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [profile, setProfile] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [waRow, setWaRow] = useState(null);
  const [waFile, setWaFile] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", { params: { type: config.type } });
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setEditRow(null);
      setFormOpen(true);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const convert = async (row) => {
    try {
      await api.post(`/transactions/${row.id}/convert`);
      toast.success(`Converted to ${config.convertible}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/transactions/${deleteRow.id}`);
      toast.success(`${config.singular} deleted, stock restored`);
      setDeleteRow(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const totals = rows.reduce(
    (a, r) => ({
      than: a.than + (Number(r.total_than) || 0),
      meter: a.meter + (Number(r.total_meter) || 0),
      amount: a.amount + (Number(r.total_amount) || 0),
      grand: a.grand + (Number(r.grand_total ?? r.total_amount) || 0),
    }),
    { than: 0, meter: 0, amount: 0, grand: 0 }
  );

  const Stat = ({ label, value }) => (
    <div className="min-w-0 border-zinc-200 sm:border-l sm:px-4 sm:first:border-l-0 sm:first:pl-0">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
      <p className="font-mono-num truncate text-lg font-semibold sm:text-xl">{value}</p>
    </div>
  );

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band flex flex-col gap-6 rounded-sm px-5 py-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{config.effect}</p>
            <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">{config.title}</h1>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-wrap sm:items-end sm:gap-y-4" data-testid="txn-stats">
            <Stat label="Entries" value={rows.length} />
            <Stat label="Than" value={totals.than} />
            <Stat label="Meter" value={totals.meter} />
            <Stat label="Taxable" value={`₹${totals.amount.toFixed(0)}`} />
            {config.type !== "adjustment" && <Stat label="Grand total" value={`₹${totals.grand.toFixed(0)}`} />}
          </div>
        </div>

        <div className="flex justify-end py-6">
          <Button
            data-testid="txn-new-button"
            className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90"
            onClick={() => {
              setEditRow(null);
              setFormOpen(true);
            }}
          >
            <Plus size={15} className="mr-2" /> New {config.singular}
          </Button>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div data-testid="txn-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">No {config.title.toLowerCase()} yet</h3>
            <p className="mt-1 text-sm text-zinc-500">{config.hint}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r, idx) => (
              <motion.div
                key={r.id}
                data-testid={`txn-row-${r.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, delay: Math.min(idx * 0.03, 0.25) }}
                className="border border-zinc-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-head text-sm font-bold tracking-tight">
                      {r.party || config.singular}
                      {r.ref_no ? <span className="text-zinc-500"> · {r.ref_no}</span> : null}
                    </p>
                    <p className="mt-0.5 font-mono-num text-xs text-zinc-500">
                      {toDMY(r.date)} · {r.items.length} item(s) · {r.total_than} than · {r.total_meter} mtr
                      {config.convertible ? ` · ${r.status}` : ""}
                    </p>
                    {config.type !== "adjustment" && (
                      <p className="mt-1 font-mono-num text-xs text-zinc-600" data-testid={`txn-tax-line-${r.id}`}>
                        Taxable ₹{Number(r.taxable_amount ?? r.total_amount).toFixed(2)}
                        {Number(r.total_discount) ? ` · Disc ₹${Number(r.total_discount).toFixed(2)}` : ""}
                        {Number(r.tax_amount)
                          ? r.interstate
                            ? ` · IGST ₹${Number(r.igst).toFixed(2)}`
                            : ` · CGST ₹${Number(r.cgst).toFixed(2)} + SGST ₹${Number(r.sgst).toFixed(2)}`
                          : ""}
                        {Number(r.round_off) ? ` · Round off ₹${Number(r.round_off).toFixed(2)}` : ""}
                        {" · "}
                        <span className="font-semibold text-zinc-950">
                          Total ₹{Number(r.grand_total ?? r.total_amount).toFixed(2)}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {config.convertible && r.status !== "completed" && (
                      <Button
                        data-testid={`txn-convert-${r.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-sm border-zinc-950"
                        onClick={() => convert(r)}
                      >
                        <CheckCircle2 size={14} className="mr-1.5" /> Convert to {config.convertible}
                      </Button>
                    )}
                    {config.type === "sale" && (
                      <Button
                        data-testid={`txn-print-${r.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/transactions/${r.id}/invoice`, { responseType: "blob" });
                            window.open(URL.createObjectURL(res.data), "_blank");
                          } catch {
                            toast.error("Could not open the bill");
                          }
                        }}
                      >
                        <Printer size={14} className="mr-1.5" /> Print bill
                      </Button>
                    )}
                    {config.type === "sale" && (
                      <Button
                        data-testid={`txn-whatsapp-${r.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-sm border-[#128C7E] text-[#0f7568]"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/transactions/${r.id}/invoice`, { responseType: "blob" });
                            setWaFile(
                              new File([res.data], `bill-${r.reference_no || r.id}.pdf`, {
                                type: "application/pdf",
                              })
                            );
                            setWaRow(r);
                          } catch {
                            toast.error("Could not prepare the bill");
                          }
                        }}
                      >
                        <MessageCircle size={14} className="mr-1.5" /> WhatsApp
                      </Button>
                    )}
                    {config.editable !== false && (
                      <Button
                        data-testid={`txn-edit-${r.id}`}
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => {
                          setEditRow(r);
                          setFormOpen(true);
                        }}
                      >
                        <Pencil size={14} className="mr-1.5" /> Edit
                      </Button>
                    )}
                    <Button
                      data-testid={`txn-delete-${r.id}`}
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-600"
                      onClick={() => setDeleteRow(r)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                <div className="mt-3 divide-y divide-dashed divide-zinc-200 border-t border-dashed border-zinc-200">
                  {r.items.map((it, i) => (
                    <div key={i} className="flex flex-wrap justify-between gap-2 py-1.5 text-xs">
                      <span className="font-medium">
                        {it.quality_name}
                        {it.shade_number ? <span className="text-zinc-500"> · shade {it.shade_number}</span> : null}
                        {it.length_text ? <span className="font-mono-num text-zinc-400"> · {it.length_text}</span> : null}
                      </span>
                      <span className="font-mono-num text-zinc-600">
                        {it.bale_no ? `Bale ${it.bale_no} · ` : ""}
                        {it.lr_no ? `LR ${it.lr_no} · ` : ""}
                        {it.than} than · {it.meter} mtr{it.rate ? ` · ₹${it.rate}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <TransactionForm
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditRow(null);
        }}
        config={config}
        editRow={editRow}
        onSaved={load}
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head tracking-tight">Delete this {config.singular}?</AlertDialogTitle>
            <AlertDialogDescription>
              Any stock movement from this entry will be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="txn-delete-cancel" className="rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="txn-delete-confirm"
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <WhatsAppSendDialog
        open={!!waRow}
        onOpenChange={(o) => {
          if (!o) {
            setWaRow(null);
            setWaFile(null);
          }
        }}
        title="Send bill on WhatsApp"
        defaultCustomer={waRow?.party || ""}
        file={waFile}
        fileLabel="bill"
        message={
          waRow
            ? `Namaste ${waRow.party}, sharing your bill ${waRow.reference_no || ""} dated ${
                waRow.date || ""
              } for ₹${Number(waRow.grand_total ?? waRow.total_amount ?? 0).toLocaleString("en-IN")}. Thank you for your business.`
            : ""
        }
      />
    </AppShell>
  );
}
